import { useEffect, useState } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Flame, Target, Beef, Camera, TrendingDown, Plus, Loader2, Sparkles } from "lucide-react";
import { FreezeWallet } from "@/components/FreezeWallet";

export default function ProgressPage() {
  const [data, setData] = useState(null);
  const [weights, setWeights] = useState([]);
  const [newWeight, setNewWeight] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.progress().then(setData).catch(() => {});
    api.getWeight().then(setWeights).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const logWeight = async () => {
    if (!newWeight) return;
    setBusy(true);
    try {
      await api.addWeight(Number(newWeight));
      setNewWeight("");
      toast.success("Weight logged");
      load();
    } catch (e) {
      toast.error("Could not log weight");
    } finally {
      setBusy(false);
    }
  };

  const prof = data?.profile || {};
  const weekly = data?.weekly || [];
  const weightData = weights.map((w) => ({
    d: new Date(w.recorded_at).toLocaleDateString("en", { month: "short", day: "numeric" }),
    w: Number(w.weight),
  }));
  const dayLabels = weekly.map((w) => ({ ...w, day: new Date(w.date).toLocaleDateString("en", { weekday: "short" }) }));

  return (
    <div className="px-5 pt-8 space-y-6">
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">Progress</h1>
        <p className="text-muted-foreground text-sm mt-0.5">Your journey, at a glance.</p>
      </div>

      {data?.days_within_goal > 0 && (
        <div className="rounded-2xl bg-primary text-primary-foreground p-4 flex items-center gap-3">
          <Sparkles className="w-5 h-5 shrink-0" />
          <p className="text-sm">You've stayed within your calorie goal <b>{data.days_within_goal} day{data.days_within_goal > 1 ? "s" : ""}</b> this week. Keep it going!</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={Flame} label="Daily streak" value={`${data?.streak || 0} days`} color="#C45A39" />
        <StatCard icon={Target} label="Avg calories" value={`${data?.avg_calories || 0}`} color="#4A5D23" />
        <StatCard icon={Camera} label="Meals scanned" value={`${data?.meals_scanned || 0}`} color="#E69C24" />
        <StatCard icon={Beef} label="Total protein" value={`${data?.total_protein || 0}g`} color="#8B7355" />
      </div>

      {/* freeze wallet */}
      <FreezeWallet />

      {/* weight */}
      <div className="rounded-3xl bg-card border border-border p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-medium tracking-tight">Weight Progress</h2>
            <p className="text-xs text-muted-foreground">{prof.current_weight || "—"} kg → target {prof.target_weight || "—"} kg</p>
          </div>
          <TrendingDown className="w-5 h-5 text-primary" />
        </div>
        {weightData.length > 1 ? (
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={weightData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <XAxis dataKey="d" tick={{ fontSize: 11, fill: "#787C77" }} axisLine={false} tickLine={false} />
              <YAxis domain={["dataMin - 2", "dataMax + 2"]} tick={{ fontSize: 11, fill: "#787C77" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DC", fontSize: 12 }} />
              <Line type="monotone" dataKey="w" stroke="#4A5D23" strokeWidth={3} dot={{ r: 3, fill: "#4A5D23" }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">Log your weight to see your trend line.</p>
        )}
        <div className="flex gap-2">
          <Input data-testid="weight-input" type="number" value={newWeight} onChange={(e) => setNewWeight(e.target.value)} placeholder="Today's weight (kg)" className="h-11 rounded-xl bg-secondary/50" />
          <Button onClick={logWeight} disabled={busy} data-testid="log-weight-btn" className="rounded-xl px-5 bg-primary hover:bg-primary/90">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* weekly nutrition */}
      <div className="rounded-3xl bg-card border border-border p-5 space-y-3">
        <h2 className="font-display text-lg font-medium tracking-tight">Weekly Nutrition</h2>
        {dayLabels.some((d) => d.calories > 0) ? (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={dayLabels} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#787C77" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#787C77" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#EFECE7" }} contentStyle={{ borderRadius: 12, border: "1px solid #E5E2DC", fontSize: 12 }} />
              <Bar dataKey="calories" fill="#4A5D23" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">Your progress will appear here as you keep going.</p>
        )}
      </div>
      <div className="h-2" />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-2xl bg-card border border-border p-4">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2" style={{ background: `${color}18` }}>
        <Icon className="w-4.5 h-4.5" style={{ color }} />
      </div>
      <div className="text-xl font-display font-medium tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
